<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { Guest } from '@/types/guest';

interface Props {
  isVisible: boolean;
  questionId: string;
  answer: string;
  guests: Guest[];
}

defineProps<Props>();
const emit = defineEmits<{
  (e: 'close'): void;
}>();

const handleClose = (): void => {
  emit('close');
};

const handleClickOutside = (event: MouseEvent): void => {
  const target = event.target as HTMLElement;
  if (target.classList.contains('answer-guests-popup')) {
    handleClose();
  }
};

const handleEscapeKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    handleClose();
  }
};

const getStatusClass = (status: boolean | undefined) => {
  if (status === true) return 'answer-guests-popup__status--confirmed';
  if (status === false) return 'answer-guests-popup__status--declined';
  return 'answer-guests-popup__status--pending';
};

const getStatusIcon = (status: boolean | undefined) => {
  if (status === true) return 'pi pi-check-circle';
  if (status === false) return 'pi pi-times-circle';
  return 'pi pi-clock';
};

const getStatusText = (status: boolean | undefined) => {
  if (status === true) return 'Potwierdził';
  if (status === false) return 'Odrzucił';
  return 'Oczekuje';
};

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
});
</script>

<template>
  <div
    v-if="isVisible"
    class="answer-guests-popup"
    role="dialog"
    aria-modal="true"
    aria-labelledby="answer-guests-popup-title"
    @click="handleClickOutside"
  >
    <div class="answer-guests-popup__content">
      <div class="answer-guests-popup__header">
        <div class="answer-guests-popup__header-info">
          <h3
            id="answer-guests-popup-title"
            class="answer-guests-popup__title"
          >
            {{ questionId }}
          </h3>
          <div class="answer-guests-popup__answer-badge">
            <i class="pi pi-comment"></i>
            <span>{{ answer }}</span>
          </div>
        </div>
        <button
          class="answer-guests-popup__close-button"
          aria-label="Zamknij okno"
          @click="handleClose"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <div class="answer-guests-popup__body">
        <h4 class="answer-guests-popup__section-title">Goście ({{ guests.length }})</h4>

        <ul
          v-if="guests.length > 0"
          class="answer-guests-popup__guest-list"
        >
          <li
            v-for="guest in guests"
            :key="guest.id"
            class="answer-guests-popup__guest-item"
          >
            <div class="answer-guests-popup__guest-name">
              <i class="pi pi-user"></i>
              <span>{{ guest.name }} {{ guest.surname }}</span>
            </div>
            <div
              class="answer-guests-popup__status"
              :class="getStatusClass(guest.presence)"
            >
              <i :class="getStatusIcon(guest.presence)"></i>
              <span>{{ getStatusText(guest.presence) }}</span>
            </div>
          </li>
        </ul>

        <div
          v-else
          class="answer-guests-popup__empty"
        >
          <i class="pi pi-info-circle"></i>
          <span>Brak gości z tą odpowiedzią</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.answer-guests-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba($black, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fade-in 0.3s ease-out;

  &__content {
    background-color: $white;
    padding: 2.5rem;
    border-radius: 16px;
    max-width: 600px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba($black, 0.2);
    position: relative;
    animation: slide-up 0.3s ease-out;
    scrollbar-width: thin;
    scrollbar-color: $brown-300 $white;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: $white;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $brown-300;
      border-radius: 4px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid $brown-600;
  }

  &__header-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;
  }

  &__title {
    color: $black-300;
    font-size: 2.4rem;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.5px;
    overflow-wrap: break-word;
  }

  &__answer-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 1rem;
    background-color: $brown-600;
    border-radius: 8px;
    color: $black-300;
    font-size: 1.5rem;
    font-weight: 500;
    width: fit-content;
    max-width: 100%;

    i {
      color: $brown-200;
      font-size: 1.6rem;
      flex-shrink: 0;
    }

    span {
      overflow-wrap: break-word;
    }
  }

  &__close-button {
    background: none;
    border: none;
    color: $brown-200;
    cursor: pointer;
    padding: 0.75rem;
    font-size: 2.4rem;
    transition: all 0.2s ease;
    border-radius: 50%;
    width: 3.2rem;
    height: 3.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      color: $brown-100;
      background-color: $brown-600;
      transform: rotate(90deg);
    }

    &:focus {
      outline: 2px solid $brown-200;
      outline-offset: 2px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__section-title {
    color: $black-300;
    font-size: 1.8rem;
    margin: 0;
    font-weight: 600;
  }

  &__guest-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__guest-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background-color: $brown-600;
    border-radius: 12px;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  &__guest-name {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: $black-300;
    font-size: 1.6rem;
    font-weight: 500;
    min-width: 0;

    i {
      color: $brown-200;
      font-size: 1.6rem;
      flex-shrink: 0;
    }

    span {
      overflow-wrap: break-word;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1.3rem;
    white-space: nowrap;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
    }

    &--confirmed {
      background-color: $status-green-light;
      color: $status-green;
    }

    &--declined {
      background-color: $status-red-light;
      color: $status-red;
    }

    &--pending {
      background-color: $status-yellow-light;
      color: $status-yellow;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    color: $brown-100;
    font-style: italic;
    padding: 2.5rem;
    background-color: $white;
    border-radius: 12px;
    text-align: center;
    border: 2px dashed $brown-300;

    i {
      color: $brown-200;
      font-size: 3rem;
    }

    span {
      font-size: 1.6rem;
    }
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slide-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: $media-max-phone) {
  .answer-guests-popup {
    &__content {
      padding: 1.5rem;
      width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 0;
    }

    &__guest-item {
      flex-direction: column;
      align-items: flex-start;
    }

    &__status {
      white-space: normal;
    }
  }
}
</style>
